import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PermissionTooltip from './PermissionTooltip'
import SettingItem from './SettingItem'
import { SETTINGS_CONFIG } from './utils'

import styles from './styles.module.scss'

import OpenLinkIcon from '@/assets/icons/openLink.svg?react'
import Button from '@/components/Button'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { useAccessKey } from '@/hooks/useAccess'
import { useAnalytics } from '@/hooks/useAnalytics'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { AppDispatch } from '@/store'
import {
  connectSSOThunk,
  selectCurrentEnterprise,
  selectIsAdmin,
  selectIsSSOConnected,
  selectIsSSOValidating,
  selectPlanType,
  setupSCIMThunk,
  setupSSOThunk,
} from '@/store/features/user/userSlice'
import { EnterpriseSettings, PlanType, SettingType } from '@/types'
import { EventName } from '@/utils/eventDefinitions'

interface SSOSectionProps {
  optimisticSettings: EnterpriseSettings
  toggleSettings: (key: keyof EnterpriseSettings, checked: boolean) => void
}

interface ConnectSsoOptions {
  showSuccessToast?: boolean
  showErrorToast?: boolean
  markAttempted?: boolean
}

const SSOSection = (props: SSOSectionProps) => {
  const { optimisticSettings, toggleSettings } = props
  const isAdmin = useSelector(selectIsAdmin)
  const planType = useSelector(selectPlanType)
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation) !== null
  const ssoAccess = useAccessKey('access.sso')
  const enterprise = useSelector(selectCurrentEnterprise)
  const isSSOConnected = useSelector(selectIsSSOConnected)
  const isSSOValidating = useSelector(selectIsSSOValidating)
  const dispatch = useDispatch<AppDispatch>()
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()
  const [hasAttemptedConnection, setHasAttemptedConnection] = useState(false)
  const [hasAutoCheckedConnection, setHasAutoCheckedConnection] = useState(false)
  const [isSCIMLoading, setIsSCIMLoading] = useState(false)

  const handleSetupSSO = useCallback(async () => {
    try {
      await dispatch(setupSSOThunk()).unwrap()
      trackEvent?.(EventName.SSOSetupCompleted, undefined)
    } catch {
      updateToast('Failed to set up SSO. Please try again.', 'error')
    }
  }, [dispatch, trackEvent, updateToast])

  const handleSetupSCIM = useCallback(async () => {
    try {
      setIsSCIMLoading(true)
      await dispatch(setupSCIMThunk()).unwrap()
      trackEvent?.(EventName.SCIMSetupCompleted, undefined)
    } catch {
      updateToast('Failed to set up SCIM. Please try again.', 'error')
    } finally {
      setIsSCIMLoading(false)
    }
  }, [dispatch, trackEvent, updateToast])

  const handleConnectSSO = useCallback(
    async ({ showSuccessToast = true, showErrorToast = true, markAttempted = true }: ConnectSsoOptions = {}) => {
      if (markAttempted) {
        setHasAttemptedConnection(true)
      }
      try {
        const result = await dispatch(connectSSOThunk()).unwrap()
        if (showSuccessToast && result.provider) {
          updateToast('Single sign-on is active.', 'success')
        }
      } catch {
        if (showErrorToast) {
          updateToast('Unable to connect to your SSO provider. Check your SSO configuration and try again.', 'error')
        }
      }
    },
    [dispatch, updateToast]
  )

  const ssoButtonTitle = isSSOConnected ? 'Manage SSO' : isSSOValidating ? 'Finish setup' : 'Configure SSO'
  const isScimBlockedBySsoState = !isSSOConnected
  const isScimButtonDisabled = !isAdmin || isSCIMLoading || isScimBlockedBySsoState
  const scimButton = (
    <Button
      title="Configure SCIM"
      isDisabled={isScimButtonDisabled}
      isLoading={isSCIMLoading}
      onClick={handleSetupSCIM}
      type="dark"
      className={styles.controlButton}
    />
  )

  // Reset connection attempt flag when enterprise changes
  useEffect(() => {
    setHasAttemptedConnection(false)
    setHasAutoCheckedConnection(false)
  }, [enterprise?.id])

  // Auto-check SSO connection once per enterprise load without toasts.
  useEffect(() => {
    if (!isAdmin || !enterprise) {
      return
    }
    if (isSSOConnected || isSSOValidating || hasAutoCheckedConnection) {
      return
    }
    setHasAutoCheckedConnection(true)
    void handleConnectSSO({ showSuccessToast: false, showErrorToast: false, markAttempted: false })
  }, [isAdmin, enterprise, handleConnectSSO, isSSOConnected, isSSOValidating, hasAutoCheckedConnection])

  const ssoRowDescription = isSSOValidating
    ? 'SSO setup is in progress. Finish setup to activate single sign-on.'
    : hasAttemptedConnection && !isSSOConnected
      ? 'Unable to connect to your SSO provider. Check your SSO configuration and try again.'
      : undefined

  // Rendering the upgrade row before the key loads would tell Enterprise admins to upgrade.
  if (ssoAccess.status === 'pending') {
    return null
  }
  const ssoAllowed = ssoAccess.status === 'ready' ? ssoAccess.enabled : planType === PlanType.Enterprise
  // View as Admin hides the upgrade prompt rather than showing a locked row.
  if (!ssoAllowed && impersonating) {
    return null
  }

  return (
    <>
      {!ssoAllowed ? (
        <div className={styles.settingsItem}>
          <div className={styles.toggleLabel}>SSO / SAML Authentication</div>
          <Tooltip text="Upgrade to Enterprise plan to use this feature" placement="top" hideArrow>
            <div className={`${styles.emailLink} ${styles.disabled}`}>
              <OpenLinkIcon />
            </div>
          </Tooltip>
        </div>
      ) : (
        <>
          {/* SettingItem still locks enterpriseOnly settings by planType; it agrees with access.sso today. */}
          {isSSOConnected && (
            <SettingItem
              config={SETTINGS_CONFIG[SettingType.enforceSSO]}
              optimisticSettings={optimisticSettings}
              toggleSettings={toggleSettings}
              isAdmin={isAdmin}
              labelDescription={
                <>
                  Whether enforced or not, your <strong>single sign-on is active</strong>, and members can sign in with
                  it.
                </>
              }
            />
          )}
          <div className={`${styles.settingsItem} ${ssoRowDescription ? styles.alignTop : ''}`}>
            {ssoRowDescription ? (
              <div className={styles.labelBlock}>
                <div className={styles.toggleLabel}>SSO / SAML Authentication</div>
                <div className={styles.labelDescription}>{ssoRowDescription}</div>
              </div>
            ) : (
              <div className={styles.toggleLabel}>SSO / SAML Authentication</div>
            )}
            <PermissionTooltip>
              <Button
                title={ssoButtonTitle}
                isDisabled={!isAdmin}
                onClick={handleSetupSSO}
                type="dark"
                className={styles.controlButton}
              />
            </PermissionTooltip>
          </div>
          <div className={styles.settingsItem}>
            <div className={styles.toggleLabel}>SCIM Provisioning</div>
            <PermissionTooltip>
              {isAdmin && isScimBlockedBySsoState ? (
                <Tooltip text="Connect and activate SSO to enable SCIM provisioning." placement="top" hideArrow>
                  {scimButton}
                </Tooltip>
              ) : (
                scimButton
              )}
            </PermissionTooltip>
          </div>
          <div className={styles.settingsItem}>
            <div className={styles.toggleLabel}>Changed your SSO configuration? Refresh your SSO connection.</div>
            <PermissionTooltip>
              <Button
                title="Refresh"
                isDisabled={!isAdmin}
                onClick={() => handleConnectSSO()}
                type="dark"
                className={styles.controlButton}
              />
            </PermissionTooltip>
          </div>
        </>
      )}
    </>
  )
}

export default SSOSection
